'use client';

import { useLoader } from '@commerce-webcomponents/ui';
import { ModularBlockComponent } from '@commerce-webcomponents/ui-composite';
import { NextgenImage, useNextgenRouter, useSession } from '@nextgen-web-framework/all';
import useLivePreview from '../../../../../live-preview/useLivePreview';
import styles from './FlexTemplatePage.module.scss';
import Layout from 'app/components/Layout/Layout';
import { BasePageProps, CMSTransformerDataType } from 'types/common';
import { TemplateType, getComponentStylesConfig } from 'utils/componentsStylingConfigs';
import { sanitizationConfig } from 'utils/sanitizationUtils';
import PageContentErrorHandler from 'app/components/PageContent/PageContentErrorHandler';
import usePageContent from 'app/components/PageContent/usePageContent';
import { useInternalNavigationTracker } from 'app/components/providers/InternalNavigationTrackerProvider/InternalNavigationTrackerProvider';

export interface FlexTemplatePageProps extends BasePageProps {
  params: CMSTransformerDataType;
}

const FlexTemplatePage = (props: FlexTemplatePageProps) => {
  const config = ConfigService.getConfigs();
  const ASSETS_BASE_PATH = config.NEXT_ASSETS_BASE_PATH;
  const router = useNextgenRouter();
  const { session } = useSession();
  const { showLoader, hideLoader } = useLoader();

  const { hasNavigatedInternally } = useInternalNavigationTracker();

  const flexTemplateContent = usePageContent({
    initialData: props?.data,
    locale: props?.locale as string,
    uId: 'flex_template',
    queryUrl: `/landing/${props?.params?.slug[0]}`,
    enableFlag: config.NEXT_PUBLIC_ENABLE_AMWAY_RESTRICTION_FLEX_TEMPLATE as string,
    userType: session?.type,
  });
  const [pageData] = useLivePreview(flexTemplateContent.data, flexTemplateContent?.data?.seo_config);
  const componentStylesConfig = getComponentStylesConfig(TemplateType.FLEX);
  const header = pageData?.layout?.header;
  const footer = pageData?.layout?.footer;
  if (flexTemplateContent.hasError || flexTemplateContent.notFound) {
    hideLoader();
  } else if (!Object.keys(pageData)?.length) {
    showLoader();
    return null;
  } else {
    hideLoader();
  }

  const backClickHandler = () => {
    if (hasNavigatedInternally) {
      router.back();
    } else {
      router.push('/');
    }
  };

  return (
    <PageContentErrorHandler content={flexTemplateContent} notFoundData={props?.notFoundData}>
      <Layout
        hasTopMobileHeader={true}
        headerProps={{
          isBackNavigation: false,
          isStaticHeader: true,
          headerElement: (
            <div className={styles['amway-logo']}>
              {' '}
              <NextgenImage
                data-testid="amway-logo-testid"
                onClick={() => {
                  router.push('/');
                }}
                src={`${ASSETS_BASE_PATH}/images/AmwayFullLogo-Black.svg`}
                alt={'Amway Logo'}
              />
            </div>
          ),
        }}
        hideLocationSelector={true}
        header={header}
        footer={footer}
        backHandler={backClickHandler}
      >
        {pageData.content_section && (
          <ModularBlockComponent
            sanitizeComponentsConfig={sanitizationConfig}
            data={pageData.content_section}
            componentStylesConfig={componentStylesConfig}
          />
        )}
      </Layout>
    </PageContentErrorHandler>
  );
};

export default FlexTemplatePage;
